import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { environment } from 'src/environments/environment';
import { lastValueFrom } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.component';

export type TabKey = 'Application' | 'Contacts' | 'BankAccounts' | 'Workflow';

type VendorCategoryLeaf = {
  id: string;
  label: string;
};

type VendorSubCategoryNode = {
  id: string;
  label: string;
  items: VendorCategoryLeaf[];
};

type VendorCategoryNode = {
  id: string;
  label: string;
  subCategories: VendorSubCategoryNode[];
};

@Component({
  selector: 'app-vendor-details',
  templateUrl: './vendor-details.component.html',
  styleUrls: ['./vendor-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VendorDetailsComponent implements OnInit {
  AppConstants = AppConstants;
  vendorId: string = '';
  childTaskId: string | null = null;
  childTaskToken: string | null = null;
  isWorkflowTaskView = false;

  activeTab: TabKey = 'Application';
  currentStep = 0;

  applicationData: any = null;
  contactsData: any = null;
  workflowData: any = null;
  bankAccounts: any[] = [];
  selectedCategoryTreeData: VendorCategoryNode[] = [];
  expandedCategoryIds = new Set<string>();
  expandedSubCategoryIds = new Set<string>();

  loadedTabs = new Set<TabKey>();

  showContactModal = false;
  contactModalMode: 'add' | 'edit' = 'add';
  editingContactId: number | null = null;
  showPasswordModal = false;
  passwordTargetContact: any = null;
  showPassword = false;
  showConfirmPassword = false;
  showRemoveContactModal = false;
  removeTargetContact: any = null;
  showRejectModal = false;
  rejectTargetType: 'vendor' | 'bank' | 'workflowTask' | 'changeRequest' = 'vendor';
  rejectTargetBankAccount: any = null;
  rejectTargetWorkflowTask: any = null;
  rejectionComments = '';
  showWorkflowTaskModal = false;
  workflowChildTaskForm = {
    assignedToEmail: '',
    title: '',
    description: ''
  };
  contactForm = {
    name: '',
    title: '',
    email: '',
    mobileNumber: '',
    landlineNumber: '',
    contactType: 'Secondary',
    password: '',
    confirmPassword: ''
  };
  passwordForm = {
    password: '',
    confirmPassword: ''
  };

  readonly organizationSteps = [
    'Supplier Information',
    'Company Ownership',
    'Financial Details',
    'Capability & Experience',
    'Supporting Documents',
    'Warehousing & Storage',
    'Other'
  ];

  readonly individualSteps = [
    'Personal Information',
    'Business Information',
    'Tax & Legal',
    'Supporting Documents',
    'Other'
  ];

  get steps(): string[] {
    return this.isIndividualVendor ? this.individualSteps : this.organizationSteps;
  }

  get isIndividualVendor(): boolean {
    const vendorType = this.getWorkflowValue(this.applicationData, ['vendorType', 'VendorType']);
    return vendorType?.toString().trim().toLowerCase() === 'individual';
  }

  private _loaderService = inject(LoaderService);
  private apiService = inject(ApiService);
  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private toasterService = inject(ToasterService);
  private dialog = inject(MatDialog);

  ngOnInit(): void {
    this.vendorId = this._route.snapshot.params['vendorId'];
    this.childTaskId = this._route.snapshot.queryParamMap.get('taskId');
    this.childTaskToken = this._route.snapshot.queryParamMap.get('taskToken');
    this.isWorkflowTaskView = !!this.childTaskId && !!this.childTaskToken;
    if (this.vendorId) {
      if (this.isWorkflowTaskView) {
        void this.loadPublicWorkflowTaskView();
      } else {
        this.loadTab('Application');
      }
    }
  }

  private async loadPublicWorkflowTaskView(): Promise<void> {
    this.activeTab = 'Application';
    await this.fetchSection('Application');
    this.loadedTabs.add('Application');
    await this.fetchBankAccounts();
    this.loadedTabs.add('BankAccounts');
    await this.fetchSection('Workflow');
    this.loadedTabs.add('Workflow');
  }

  async loadTab(tab: TabKey): Promise<void> {
    this.activeTab = tab;
    if (this.loadedTabs.has(tab)) {
      this.cdr.markForCheck();
      return;
    }
    this.loadedTabs.add(tab);

    if (tab === 'BankAccounts') {
      await this.fetchBankAccounts();
    } else {
      await this.fetchSection(tab);
    }
  }

  private async fetchSection(section: string): Promise<void> {
    this._loaderService.showLoader();
    try {
      const url = this.buildVendorDetailsSectionUrl(section);

      if (section === 'Application') {
        let response: any;
        let categoryLookups: { categories: any[]; subCategories: any[]; subSubCategories: any[] };

        if (this.isWorkflowTaskView) {
          response = await lastValueFrom(this.apiService.getByUrl(url));
          categoryLookups = { categories: [], subCategories: [], subSubCategories: [] };
        } else {
          [response, categoryLookups] = await Promise.all([
            lastValueFrom(this.apiService.getByUrl(url)),
            this.fetchCategoryTreeLookups()
          ]);
        }

        const data = response?.data || response?.Data || null;
        this.applicationData = data;
        console.log('[VendorDetails] Fetched Application Data:', data);
        if (this.currentStep >= this.steps.length) {
          this.currentStep = 0;
        }
        if (this.isWorkflowTaskView && this.bankAccounts.length === 0) {
          this.bankAccounts = this.extractPublicBankAccounts(data);
        }
        this.selectedCategoryTreeData = this.buildSelectedCategoryTreeFromLookups(data, categoryLookups);
        this.initializeCategoryTreeExpansion();
      } else {
        const response: any = await lastValueFrom(this.apiService.getByUrl(url));
        const data = response?.data || response?.Data || null;

        if (section === 'Contacts') this.contactsData = data;
        else if (section === 'Workflow') this.workflowData = data;
      }

      this.cdr.markForCheck();
    } catch (error) {
      console.error(`[VendorDetails] Error fetching ${section}:`, error);
      this.cdr.markForCheck();
    } finally {
      this._loaderService.hideLoader();
    }
  }

  private async fetchBankAccounts(): Promise<void> {
    this._loaderService.showLoader();
    try {
      const url = this.isWorkflowTaskView
        ? this.buildVendorDetailsSectionUrl('BankAccounts')
        : `${environment.API_URL}/api/${AppConstants.CONTROLLERS.BankAPI}/${AppConstants.ACTIONS.Admin_GetBankAccountsByVendorID}?vendorId=${this.vendorId}`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));
      const raw = this.isWorkflowTaskView
        ? this.extractPublicBankAccounts(response?.data || response?.Data)
        : response?.data || response?.Data || [];
      this.bankAccounts = Array.isArray(raw) ? raw : (raw ? [raw] : []);
      this.cdr.markForCheck();
    } catch (error) {
      console.error('[VendorDetails] Error fetching bank accounts:', error);
      this.bankAccounts = [];
      this.cdr.markForCheck();
    } finally {
      this._loaderService.hideLoader();
    }
  }

  canApproveBankAccount(bankAccount: any): boolean {
    const status = (this.getWorkflowValue(bankAccount, ['status', 'Status']) || '').toString().toLowerCase();
    const canApprove = this.getWorkflowValue(bankAccount, ['canCurrentUserApprove', 'CanCurrentUserApprove']) === true;
    return canApprove && ['pending', 'submitted', 'draft', '1'].includes(status);
  }

  isBankAccountRejected(bankAccount: any): boolean {
    const status = (this.getWorkflowValue(bankAccount, ['status', 'Status']) || '').toString().toLowerCase();
    return ['rejected', 'cancelled', '3'].includes(status);
  }

  getBankAccountRejectionComments(bankAccount: any): string {
    const directReason = this.getWorkflowValue(bankAccount, ['rejectionReason', 'RejectionReason']);
    if (directReason) {
      return directReason;
    }

    const history: any[] = this.getWorkflowValue(bankAccount, ['bankWorkflowHistory', 'BankWorkflowHistory']) || [];
    const rejectHistory = history
      .slice()
      .reverse()
      .find((item: any) => (this.getWorkflowValue(item, ['action', 'Action']) || '').toString().toLowerCase() === 'reject');

    return this.getWorkflowValue(rejectHistory, ['comments', 'Comments']) || '';
  }

  canRejectBankAccount(bankAccount: any): boolean {
    return this.canApproveBankAccount(bankAccount);
  }

  async approveBankAccount(bankAccount: any): Promise<void> {
    if (!this.canApproveBankAccount(bankAccount)) {
      return;
    }

    const accountId = this.getBankAccountApprovalId(bankAccount);
    if (!accountId) {
      this.toasterService.showError('Bank account number is missing', 'Error');
      return;
    }

    this._loaderService.showLoader();
    try {
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.BankAPI}/${AppConstants.ACTIONS.Admin_ApproveBankAccount}?vendorId=${this.vendorId}&accountId=${encodeURIComponent(accountId)}`;
      const response: any = await lastValueFrom(this.apiService.postByUrl(url, {}));

      if (response?.isSuccess || response?.IsSuccess) {
        this.toasterService.showSuccess('Bank account approval submitted successfully', 'Success');
        this.loadedTabs.delete('BankAccounts');
        await this.fetchBankAccounts();
        this.loadedTabs.add('BankAccounts');
      } else {
        this.toasterService.showError(response?.errorMessageEn || response?.ErrorMessageEn || 'Failed to approve bank account', 'Error');
      }
    } catch (error: any) {
      this.toasterService.showError(error?.error?.errorMessageEn || error?.error?.ErrorMessageEn || 'An error occurred while approving bank account', 'Error');
    } finally {
      this._loaderService.hideLoader();
      this.cdr.markForCheck();
    }
  }

  openRejectBankAccount(bankAccount: any): void {
    if (!this.canRejectBankAccount(bankAccount)) {
      return;
    }

    this.rejectTargetType = 'bank';
    this.rejectTargetBankAccount = bankAccount;
    this.rejectionComments = '';
    this.showRejectModal = true;
    this.cdr.markForCheck();
  }

  getBankWorkflowTimeline(bankAccount: any): any[] {
    const history: any[] = this.getWorkflowValue(bankAccount, ['bankWorkflowHistory', 'BankWorkflowHistory']) || [];
    const steps: any[] = this.getWorkflowValue(bankAccount, ['bankWorkflowStepConfigurations', 'BankWorkflowStepConfigurations']) || [];
    const bankStatus = (this.getWorkflowValue(bankAccount, ['status', 'Status']) || '').toString().toLowerCase();
    const isApproved = bankStatus === 'approved';
    const sortedHistory = history
      .slice()
      .sort((a: any, b: any) => new Date(this.getWorkflowValue(a, ['actionDate', 'ActionDate']) || 0).getTime() - new Date(this.getWorkflowValue(b, ['actionDate', 'ActionDate']) || 0).getTime());

    if (steps.length === 0) {
      return sortedHistory.map((item: any) => this.mapWorkflowHistoryItem(item));
    }

    const approvalHistory = sortedHistory.filter((item: any) =>
      (this.getWorkflowValue(item, ['action', 'Action']) || '').toString().toLowerCase() === 'approve'
    );
    const rejectHistory = sortedHistory.find((item: any) =>
      (this.getWorkflowValue(item, ['action', 'Action']) || '').toString().toLowerCase() === 'reject'
    );
    const otherHistory = sortedHistory
      .filter((item: any) => !approvalHistory.includes(item) && item !== rejectHistory)
      .map((item: any) => this.mapWorkflowHistoryItem(item));

    const stepItems = steps.map((step: any, index: number) => {
      const historyItem = approvalHistory[index];
      const rejectionItem = !historyItem && rejectHistory && index === approvalHistory.length ? rejectHistory : null;
      const stepOrder = this.getWorkflowValue(step, ['stepOrder', 'StepOrder']);
      const stepName = this.getWorkflowValue(step, ['stepName', 'StepName']) || `Approval Level ${stepOrder || index + 1}`;
      const responsibleEmail = this.getWorkflowValue(step, ['responsibleEmail', 'ResponsibleEmail']);
      const done = !!historyItem || !!rejectionItem || isApproved;

      return {
        label: stepName.toString().toUpperCase(),
        by: done ? this.getWorkflowValue(historyItem || rejectionItem, ['actionBy', 'ActionBy']) || responsibleEmail : `Waiting for ${responsibleEmail}`,
        date: historyItem || rejectionItem ? this.getWorkflowValue(historyItem || rejectionItem, ['actionDate', 'ActionDate']) : null,
        done,
        rejected: !!rejectionItem,
        assignedTo: responsibleEmail,
        statusText: historyItem || rejectionItem ? this.getWorkflowStatusText(historyItem || rejectionItem) : (isApproved ? 'Approved' : ''),
        comments: historyItem || rejectionItem ? this.getWorkflowValue(historyItem || rejectionItem, ['comments', 'Comments']) : ''
      };
    });

    return [...otherHistory, ...stepItems];
  }

  getCurrentBankApprovalStepName(bankAccount: any): string {
    const steps: any[] = this.getWorkflowValue(bankAccount, ['bankWorkflowStepConfigurations', 'BankWorkflowStepConfigurations']) || [];
    if (steps.length === 0) {
      return 'current approval step';
    }

    const currentLevel = this.getWorkflowValue(bankAccount, ['currentWorkflowStepLevel', 'CurrentWorkflowStepLevel']);
    const step = currentLevel
      ? steps.find((item: any) => Number(this.getWorkflowValue(item, ['stepOrder', 'StepOrder'])) === Number(currentLevel)) || steps[0]
      : steps[0];

    return this.getWorkflowValue(step, ['stepName', 'StepName']) || 'current approval step';
  }

  getBankAccountDocuments(bankAccount: any): any[] {
    const directDocuments = this.getWorkflowValue(bankAccount, ['document', 'Document', 'documents', 'Documents']);
    if (Array.isArray(directDocuments)) {
      return directDocuments.filter((doc: any) => !!doc);
    }
    return [];
  }

  getBankAccountDocumentGroups(bankAccount: any): { label: string; files: any[] }[] {
    const map = new Map<string, { label: string; files: any[] }>();

    this.getBankAccountDocuments(bankAccount).forEach((doc: any) => {
      const label = this.getDocumentLabel(doc);
      const key = label.toLowerCase();

      if (!map.has(key)) {
        map.set(key, { label, files: [] });
      }

      map.get(key)!.files.push(doc);
    });

    return Array.from(map.values());
  }

  getBankAccountDisplayValue(bankAccount: any, keys: string[]): string {
    return this.val(this.getWorkflowValue(bankAccount, keys));
  }

  getBankDisplayName(bankAccount: any): string {
    const selectedName = this.getWorkflowValue(bankAccount, ['name', 'Name', 'bankName', 'BankName']);
    const otherBankName = this.getWorkflowValue(bankAccount, ['otherBankName', 'OtherBankName']);

    if (selectedName?.toString().trim().toLowerCase() === 'other' && otherBankName) {
      return otherBankName;
    }

    return this.val(selectedName || otherBankName);
  }

  getDocumentName(document: any): string {
    return this.getWorkflowValue(document, ['name', 'Name', 'fileName', 'FileName']) || 'Document';
  }

  getDocumentLabel(document: any): string {
    const attachmentFor = (this.getWorkflowValue(document, ['attachmentFor', 'AttachmentFor']) || '').toString();
    if (attachmentFor.toLowerCase() === 'bankcertificate') {
      return 'Bank Certificate';
    }
    if (attachmentFor.toLowerCase() === 'document') {
      return 'Company Bank Details';
    }
    return attachmentFor || 'Document';
  }

  getDocumentUrl(document: any): string {
    return this.getWorkflowValue(document, ['fileContent', 'FileContent', 'filePath', 'FilePath']) || '';
  }

  getDocumentExpiryDate(document: any): any {
    return this.getWorkflowValue(document, ['documentExpiryDate', 'DocumentExpiryDate', 'expiryDate', 'ExpiryDate']);
  }

  private getBankAccountApprovalId(bankAccount: any): string {
    return this.getWorkflowValue(bankAccount, ['accountNumber', 'AccountNumber']) || '';
  }

  get vendorName(): string {
    return this.applicationData?.name || this.applicationData?.vendorName || this.contactsData?.name || '';
  }

  private getRawVendorStatus(): string {
    return this.applicationData?.status || this.applicationData?.Status || this.contactsData?.status || this.contactsData?.Status || this.workflowData?.status || this.workflowData?.Status || '';
  }

  normalizeVendorStatus(status: any): string {
    const normalizedStatus = (status ?? '').toString().replace(/\s+/g, '').toLowerCase();
    if (normalizedStatus === 'changerequest' || normalizedStatus === 'changerequested') {
      return 'Change Request';
    }

    return (status ?? '').toString();
  }

  getVendorStatusClass(status: any): string {
    const normalizedStatus = (status ?? '').toString().replace(/\s+/g, '').toLowerCase();
    if (normalizedStatus === 'changerequest' || normalizedStatus === 'changerequested') {
      return 'change-request';
    }

    return normalizedStatus;
  }

  get vendorStatus(): string {
    return this.normalizeVendorStatus(this.getRawVendorStatus());
  }

  get vendorStatusClass(): string {
    return this.getVendorStatusClass(this.getRawVendorStatus());
  }

  get erpVendorId(): string {
    return this.applicationData?.erpVendorID || this.contactsData?.erpVendorID || '';
  }

  isStepComplete(index: number): boolean {
    if (!this.applicationData) return false;
    const d = this.applicationData;

    if (this.isIndividualVendor) {
      switch (index) {
        case 0: return !!(
          d.name || d.vendorName || d.qatid || d.passport || d.nationalIdPassport ||
          d.dateOfBirth || d.gender || d.residentialAddress || d.mobileNumber || d.emailAddress
        );
        case 1: return !!(d.tradingName || d.typeOfBusinessActivity || d.yearsOfExperience || d.descriptionOfGoodsServices || d.description);
        case 2: return !!(d.taxIdentificationNumber || d.taxId || d.vatRegistrationNumber || d.vatNumber || d.countryOfTaxResidence || d.taxResidenceCountry);
        case 3: return !!(d.attachments?.length);
        case 4: return !!(d.otherInformation);
        default: return false;
      }
    }

    switch (index) {
      case 0: return !!(d.vendorType || d.name);
      case 1: return !!(d.partners?.length || d.managementTeam?.length || d.ownerName);
      case 2: return !!(d.currency || d.annualTurnovers?.length);
      case 3: return !!(d.coreProductsServices || d.authorizedRepresentatives?.length);
      case 4: return !!(d.attachments?.length);
      case 5: return !!(d.warehousingStorageDetails || d.transportFreightForwardingDetails);
      case 6: return !!(d.otherInformation || d.taxIdentificationNumber);
      default: return false;
    }
  }

  get supportingDocGroups(): { label: string; files: any[] }[] {
    const atts: any[] = this.applicationData?.attachments || [];
    const excluded = /^authorizedRepFile/i;
    const map = new Map<string, any[]>();
    atts.forEach(a => {
      if (excluded.test(a.attachmentFor)) return;
      if (!map.has(a.attachmentFor)) map.set(a.attachmentFor, []);
      map.get(a.attachmentFor)!.push(a);
    });
    return Array.from(map.entries()).map(([label, files]) => ({ label, files }));
  }

  getApplicationDisplayValue(keys: string[]): string {
    return this.val(this.getWorkflowValue(this.applicationData, keys));
  }


  private async fetchCategoryTreeLookups(): Promise<{ categories: any[]; subCategories: any[]; subSubCategories: any[] }> {
    const [cats, subs, subSubs] = await Promise.all([
      lastValueFrom(this.apiService.getByUrl(`${environment.API_URL}/api/${AppConstants.CONTROLLERS.Lookups}/categories`)),
      lastValueFrom(this.apiService.getByUrl(`${environment.API_URL}/api/${AppConstants.CONTROLLERS.Lookups}/subcategories`)),
      lastValueFrom(this.apiService.getByUrl(`${environment.API_URL}/api/${AppConstants.CONTROLLERS.Lookups}/subsubcategories`))
    ]);

    return {
      categories: this.extractArray(cats),
      subCategories: this.extractArray(subs),
      subSubCategories: this.extractArray(subSubs)
    };
  }

  private extractArray(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.Data)) return response.Data;
    return [];
  }

  private buildSelectedCategoryTreeFromLookups(
    data: any,
    lookups: { categories: any[]; subCategories: any[]; subSubCategories: any[] }
  ): VendorCategoryNode[] {
    const selectedIds = this.extractSelectedSubSubCategoryIds(data);
    if (selectedIds.length === 0) {
      return this.buildSelectedCategoryTreeFallback(data?.subSubCategory || data?.SubSubCategory || []);
    }

    const getSubSubId = (item: any) => this.pickValue(item, ['subSubCategoryId', 'SubSubCategoryId', 'subSubCategoryID', 'SubSubCategoryID', 'id', 'ID']);
    const getSubCategoryId = (item: any) => this.pickValue(item, ['subCategoryId', 'SubCategoryId', 'subCategoryID', 'SubCategoryID']);
    const getCategoryId = (item: any) => this.pickValue(item, ['categoryId', 'CategoryId', 'categoryID', 'CategoryID']);

    const matchedSubSubs = lookups.subSubCategories.filter((item: any) => selectedIds.includes(getSubSubId(item)));
    const directlySelectedSubCategories = lookups.subCategories.filter((item: any) => selectedIds.includes(getSubCategoryId(item)));
    const directlySelectedCategories = lookups.categories.filter((item: any) => selectedIds.includes(getCategoryId(item)));

    if (matchedSubSubs.length === 0 && directlySelectedSubCategories.length === 0 && directlySelectedCategories.length === 0) {
      return this.buildSelectedCategoryTreeFallback(data?.subSubCategory || data?.SubSubCategory || []);
    }

    const subCategoryIds = Array.from(new Set([
      ...matchedSubSubs.map((item: any) => getSubCategoryId(item)),
      ...directlySelectedSubCategories.map((item: any) => getSubCategoryId(item))
    ].filter(Boolean)));

    const matchedSubCategories = lookups.subCategories.filter((item: any) =>
      subCategoryIds.includes(getSubCategoryId(item))
    );

    const categoryIds = Array.from(new Set([
      ...matchedSubCategories.map((item: any) => getCategoryId(item)),
      ...directlySelectedCategories.map((item: any) => getCategoryId(item))
    ].filter(Boolean)));

    const matchedCategories = lookups.categories.filter((item: any) =>
      categoryIds.includes(getCategoryId(item))
    );

    return matchedCategories.map((category: any) => {
      const categoryId = getCategoryId(category);

      return {
        id: categoryId,
        label: this.pickValue(category, ['description', 'Description', 'name', 'Name']) || categoryId,
        subCategories: matchedSubCategories
          .filter((sub: any) => getCategoryId(sub) === categoryId)
          .map((sub: any) => {
            const subCategoryId = getSubCategoryId(sub);

            return {
              id: subCategoryId,
              label: this.pickValue(sub, ['description', 'Description', 'name', 'Name']) || subCategoryId,
              items: matchedSubSubs
                .filter((item: any) => getSubCategoryId(item) === subCategoryId)
                .map((item: any) => ({
                  id: getSubSubId(item),
                  label: this.pickValue(item, ['description', 'Description', 'name', 'Name']) || getSubSubId(item)
                }))
            };
          })
      };
    });
  }

  private extractSelectedSubSubCategoryIds(data: any): string[] {
    const rawItems: any[] = data?.subSubCategory || data?.SubSubCategory || [];
    if (!Array.isArray(rawItems)) {
      return [];
    }

    return rawItems
      .map((item: any) => {
        if (typeof item === 'string' || typeof item === 'number') {
          return String(item);
        }

        return this.pickValue(item, ['subSubCategoryId', 'SubSubCategoryId', 'subSubCategoryID', 'SubSubCategoryID', 'id', 'ID']);
      })
      .filter(Boolean);
  }

  private buildSelectedCategoryTreeFallback(rawItems: any[]): VendorCategoryNode[] {
    if (!Array.isArray(rawItems) || rawItems.length === 0) {
      return [];
    }

    const categoryMap = new Map<string, VendorCategoryNode>();

    rawItems.forEach((item: any, index: number) => {
      const categoryId = this.pickValue(item, ['categoryId', 'categoryID', 'CategoryID', 'CategoryId', 'parentCategoryId', 'parentCategoryID']) || `category-${index}`;
      const categoryLabel = this.pickValue(item, [
        'categoryDescription',
        'categoryName',
        'CategoryDescription',
        'CategoryName',
        'parentCategoryDescription',
        'parentCategoryName'
      ]) || 'Category';

      const subCategoryId = this.pickValue(item, ['subCategoryId', 'subCategoryID', 'SubCategoryID', 'SubCategoryId']) || `subcategory-${index}`;
      const subCategoryLabel = this.pickValue(item, [
        'subCategoryDescription',
        'subCategoryName',
        'SubCategoryDescription',
        'SubCategoryName'
      ]) || 'Subcategory';

      const leafId = this.pickValue(item, ['subSubCategoryId', 'subSubCategoryID', 'SubSubCategoryID', 'SubSubCategoryId', 'id']) || `leaf-${index}`;
      const leafLabel = this.pickValue(item, ['description', 'Description', 'name', 'Name']) || 'Item';

      if (!categoryMap.has(categoryId)) {
        categoryMap.set(categoryId, {
          id: categoryId,
          label: categoryLabel,
          subCategories: []
        });
      }

      const categoryNode = categoryMap.get(categoryId)!;
      let subCategoryNode = categoryNode.subCategories.find((sub) => sub.id === subCategoryId);

      if (!subCategoryNode) {
        subCategoryNode = {
          id: subCategoryId,
          label: subCategoryLabel,
          items: []
        };
        categoryNode.subCategories.push(subCategoryNode);
      }

      if (!subCategoryNode.items.some((leaf) => leaf.id === leafId)) {
        subCategoryNode.items.push({
          id: leafId,
          label: leafLabel
        });
      }
    });

    return Array.from(categoryMap.values());
  }

  toggleCategoryExpand(categoryId: string): void {
    if (this.expandedCategoryIds.has(categoryId)) {
      this.expandedCategoryIds.delete(categoryId);
    } else {
      this.expandedCategoryIds.add(categoryId);
    }
    this.cdr.markForCheck();
  }

  isCategoryExpanded(categoryId: string): boolean {
    return this.expandedCategoryIds.has(categoryId);
  }

  toggleSubCategoryExpand(subCategoryId: string): void {
    if (this.expandedSubCategoryIds.has(subCategoryId)) {
      this.expandedSubCategoryIds.delete(subCategoryId);
    } else {
      this.expandedSubCategoryIds.add(subCategoryId);
    }
    this.cdr.markForCheck();
  }

  isSubCategoryExpanded(subCategoryId: string): boolean {
    return this.expandedSubCategoryIds.has(subCategoryId);
  }

  private initializeCategoryTreeExpansion(): void {
    this.expandedCategoryIds = new Set(this.selectedCategoryTreeData.map(category => category.id));
    this.expandedSubCategoryIds = new Set(
      this.selectedCategoryTreeData.flatMap(category => category.subCategories.map(subCategory => subCategory.id))
    );
  }

  get workflowTimeline(): any[] {
    const history: any[] = this.workflowData?.workflowHistory || this.workflowData?.WorkflowHistory || [];
    const workflowStatus = (this.workflowData?.status || this.workflowData?.Status || '').toString().toLowerCase();
    const isApproved = workflowStatus === 'approved';
    const approvedBy = this.workflowData?.approvedBy || this.workflowData?.ApprovedBy;
    const approvedDate = this.workflowData?.approvedDate || this.workflowData?.ApprovedDate;

    const historyItems = history
      .map((item: any, index: number) => ({ item, index }))
      .sort((a: any, b: any) => {
        const aTime = new Date(this.getWorkflowValue(a.item, ['actionDate', 'ActionDate']) || 0).getTime();
        const bTime = new Date(this.getWorkflowValue(b.item, ['actionDate', 'ActionDate']) || 0).getTime();
        return aTime === bTime ? a.index - b.index : aTime - bTime;
      })
      .map(({ item }: any) => this.mapWorkflowHistoryItem(item));

    if (isApproved && approvedDate && !historyItems.some((item: any) => item.date === approvedDate)) {
      historyItems.push({
        label: 'APPROVED',
        by: approvedBy || '-',
        date: approvedDate,
        done: true,
        rejected: false,
        statusText: 'Approved',
        comments: ''
      });
    }

    if (workflowStatus === 'submitted') {
      const steps: any[] = this.workflowData?.workflowStepConfigurations || this.workflowData?.WorkflowStepConfigurations || [];
      const currentStep = this.getCurrentWorkflowStep();
      const currentStepOrder = Number(this.getWorkflowValue(currentStep, ['stepOrder', 'StepOrder']));
      const responsibleDisplay = this.getWorkflowStepResponsibleDisplay(currentStep) || this.currentWorkflowApprovalStepName;
      historyItems.push({
        label: 'WAITING APPROVAL',
        by: `Waiting for ${responsibleDisplay}`,
        date: null,
        done: false,
        rejected: false,
        assignedTo: responsibleDisplay,
        statusText: '',
        comments: ''
      });

      steps
        .filter((step: any) => Number(this.getWorkflowValue(step, ['stepOrder', 'StepOrder'])) > currentStepOrder)
        .sort((a: any, b: any) => Number(this.getWorkflowValue(a, ['stepOrder', 'StepOrder'])) - Number(this.getWorkflowValue(b, ['stepOrder', 'StepOrder'])))
        .forEach((step: any) => {
          const upcomingResponsibleDisplay = this.getWorkflowStepResponsibleDisplay(step)
            || this.getWorkflowValue(step, ['stepName', 'StepName'])
            || this.getWorkflowValue(step, ['responsibleEmail', 'ResponsibleEmail'])
            || 'Next approver';

          historyItems.push({
            label: 'UPCOMING',
            by: `Upcoming: ${upcomingResponsibleDisplay}`,
            date: null,
            done: false,
            rejected: false,
            assignedTo: upcomingResponsibleDisplay,
            statusText: '',
            comments: ''
          });
        });
    }

    return historyItems;
  }

  private workflowHistoryMatchesStep(historyItem: any, stepOrder: any): boolean {
    if (!stepOrder) {
      return false;
    }

    const directStepOrder = this.getWorkflowValue(historyItem, ['stepOrder', 'StepOrder']);
    if (directStepOrder && Number(directStepOrder) === Number(stepOrder)) {
      return true;
    }

    const comments = this.getWorkflowValue(historyItem, ['comments', 'Comments']) || '';
    return new RegExp(`\\bLevel\\s+${stepOrder}\\b`, 'i').test(comments);
  }

  private getWorkflowStatusText(item: any): string {
    const fromStatus = this.getWorkflowValue(item, ['fromStatus', 'FromStatus']);
    const toStatus = this.getWorkflowValue(item, ['toStatus', 'ToStatus']);
    const fromStatusDisplay = this.normalizeVendorStatus(fromStatus);
    const toStatusDisplay = this.normalizeVendorStatus(toStatus);

    if (fromStatus && toStatus && fromStatus !== toStatus) {
      return `${fromStatusDisplay} to ${toStatusDisplay}`;
    }

    return toStatusDisplay || fromStatusDisplay || '';
  }

  get isVendorRejected(): boolean {
    const status = this.getVendorStatusClass(this.workflowData?.status || this.workflowData?.Status || '');
    return status === 'rejected';
  }

  get vendorRejectionComments(): string {
    const directReason = this.workflowData?.rejectionReason || this.workflowData?.RejectionReason;
    if (directReason) {
      return directReason;
    }

    const history: any[] = this.workflowData?.workflowHistory || this.workflowData?.WorkflowHistory || [];
    const rejectHistory = history
      .slice()
      .reverse()
      .find((item: any) => (this.getWorkflowValue(item, ['action', 'Action']) || '').toString().toLowerCase() === 'reject');

    return this.getWorkflowValue(rejectHistory, ['comments', 'Comments']) || '';
  }

  private mapWorkflowHistoryItem(item: any): any {
    const action = this.getWorkflowValue(item, ['action', 'Action']) || 'Workflow Action';
    const normalizedAction = action.toString().trim().toLowerCase();

    return {
      label: this.getWorkflowHistoryLabel(normalizedAction, action.toString()),
      by: this.getWorkflowValue(item, ['actionBy', 'ActionBy']) || '-',
      date: this.getWorkflowValue(item, ['actionDate', 'ActionDate']),
      done: true,
      rejected: normalizedAction === 'reject' || normalizedAction === 'rejected',
      statusText: this.getWorkflowStatusText(item),
      comments: this.getWorkflowValue(item, ['comments', 'Comments'])
    };
  }

  private getWorkflowHistoryLabel(normalizedAction: string, fallbackAction: string): string {
    if (normalizedAction === 'reject' || normalizedAction === 'rejected') {
      return 'REJECTED';
    }
    if (normalizedAction === 'approve' || normalizedAction === 'approved') {
      return 'APPROVED';
    }
    if (normalizedAction === 'submit' || normalizedAction === 'submitted') {
      return 'SUBMIT';
    }

    return fallbackAction.toUpperCase();
  }

  get canApproveVendorWorkflow(): boolean {
    if (this.isWorkflowTaskView) {
      return false;
    }

    const status = (this.workflowData?.status || this.workflowData?.Status || '').toString().toLowerCase();
    if (!this.workflowData || status !== 'submitted') {
      return false;
    }

    if (this.hasPendingWorkflowChildTasks) {
      return false;
    }

    if (this.workflowData?.canCurrentUserApprove === true || this.workflowData?.CanCurrentUserApprove === true) {
      return true;
    }

    const currentStep = this.getCurrentWorkflowStep();
    const stepOrder = Number(this.getWorkflowValue(currentStep, ['stepOrder', 'StepOrder']));
    const requiredRole = (this.currentWorkflowRequiredRole || this.getWorkflowValue(currentStep, ['requiredRole', 'RequiredRole']) || '')
      .toString()
      .replace(/\s+/g, '')
      .toLowerCase();
    const currentUserRole = this.getCurrentUserRoleName();

    return (
      (stepOrder === 1 || requiredRole === 'procurmentofficer' || requiredRole === 'procurementofficer') && currentUserRole === 'procurementofficer'
    ) || (
      (stepOrder === 2 || requiredRole === 'secretary') && currentUserRole === 'secretary'
    ) || (
      (stepOrder === 3 || requiredRole === 'vicechairman') && currentUserRole === 'vicechairman'
    );
  }

  private buildVendorDetailsSectionUrl(section: string): string {
    if (this.isWorkflowTaskView && this.childTaskId && this.childTaskToken) {
      return `${environment.API_URL}/api/${AppConstants.CONTROLLERS.VENDOR}/${AppConstants.ACTIONS.Public_GetVendorWorkflowChildTaskDetails}?vendorId=${this.vendorId}&taskId=${encodeURIComponent(this.childTaskId)}&taskToken=${encodeURIComponent(this.childTaskToken)}&section=${section}`;
    }

    const taskParam = this.childTaskId ? `&taskId=${encodeURIComponent(this.childTaskId)}` : '';
    return `${environment.API_URL}/api/${AppConstants.CONTROLLERS.VENDOR}/${AppConstants.ACTIONS.Admin_GetVendorDetailsByID}?vendorId=${this.vendorId}&section=${section}${taskParam}`;
  }

  get canRejectVendorWorkflow(): boolean {
    return this.canApproveVendorWorkflow;
  }

  get canRequestVendorChange(): boolean {
    return this.canApproveVendorWorkflow;
  }

  get canSendWorkflowReminder(): boolean {
    if (this.isWorkflowTaskView) {
      return false;
    }

    const status = (this.workflowData?.status || this.workflowData?.Status || '').toString().toLowerCase();
    if (!this.workflowData || status !== 'submitted') {
      return false;
    }

    const currentStep = this.getCurrentWorkflowStep();
    const configuredStepOrder = this.getWorkflowValue(currentStep, ['stepOrder', 'StepOrder']);
    const currentStepOrder = Number(configuredStepOrder || this.workflowData?.currentWorkflowStepLevel || this.workflowData?.CurrentWorkflowStepLevel);
    const currentUserRole = this.getCurrentUserRoleName();

    return (currentUserRole === 'procurementofficer' && (currentStepOrder === 2 || currentStepOrder === 3))
      || (currentUserRole === 'secretary' && currentStepOrder === 3);
  }
  get workflowChildTasks(): any[] {
    const tasks = this.workflowData?.workflowChildTasks || this.workflowData?.WorkflowChildTasks || [];
    return Array.isArray(tasks) ? tasks : [];
  }

  get hasPendingWorkflowChildTasks(): boolean {
    if (this.workflowData?.hasPendingWorkflowChildTasks === true || this.workflowData?.HasPendingWorkflowChildTasks === true) {
      return true;
    }

    return this.workflowChildTasks.some(task => !this.isWorkflowChildTaskClosed(task));
  }

  get currentWorkflowRequiredRole(): string {
    return (this.workflowData?.currentWorkflowRequiredRole || this.workflowData?.CurrentWorkflowRequiredRole || '').toString();
  }

  get canCreateWorkflowChildTask(): boolean {
    const status = (this.workflowData?.status || this.workflowData?.Status || '').toString().toLowerCase();
    const requiredRole = this.currentWorkflowRequiredRole.replace(/\s+/g, '').toLowerCase();
    const currentStep = this.getCurrentWorkflowStep();
    const stepOrder = Number(this.getWorkflowValue(currentStep, ['stepOrder', 'StepOrder']));
    return !this.isWorkflowTaskView
      && status === 'submitted'
      && this.getCurrentUserRoleName() === 'procurementofficer'
      && (requiredRole === 'procurmentofficer' || requiredRole === 'procurementofficer' || stepOrder === 1);
  }

  get canManageContacts(): boolean {
    return !this.isWorkflowTaskView && this.getCurrentUserRoleName() === 'procurementofficer';
  }

  openWorkflowTaskModal(): void {
    if (!this.canCreateWorkflowChildTask) {
      return;
    }

    this.workflowChildTaskForm = {
      assignedToEmail: '',
      title: '',
      description: ''
    };
    this.showWorkflowTaskModal = true;
    this.cdr.markForCheck();
  }

  closeWorkflowTaskModal(): void {
    this.showWorkflowTaskModal = false;
    this.cdr.markForCheck();
  }

  get workflowTaskStatusText(): string {
    if (!this.hasPendingWorkflowChildTasks) {
      return '';
    }

    return 'Pending child tasks must be completed before this stage can be approved.';
  }

  get currentWorkflowApprovalStepName(): string {
    const step = this.getCurrentWorkflowStep();
    return this.getWorkflowStepResponsibleDisplay(step) || step?.stepName || step?.StepName || 'current approval step';
  }

  private getCurrentWorkflowStep(): any {
    const steps: any[] = this.workflowData?.workflowStepConfigurations || this.workflowData?.WorkflowStepConfigurations || [];
    if (!Array.isArray(steps) || steps.length === 0) {
      return null;
    }

    const currentLevel = this.workflowData?.currentWorkflowStepLevel || this.workflowData?.CurrentWorkflowStepLevel;
    if (currentLevel) {
      return steps.find((step: any) => Number(step.stepOrder || step.StepOrder) === Number(currentLevel)) || steps[0];
    }

    return steps[0];
  }

  private getWorkflowStepResponsibleDisplay(step: any): string {
    if (!step) {
      return '';
    }

    const directDisplay = this.getWorkflowValue(step, ['responsibleDisplay', 'ResponsibleDisplay']);
    if (directDisplay) {
      return directDisplay;
    }

    const stepOrder = Number(this.getWorkflowValue(step, ['stepOrder', 'StepOrder']));
    if (stepOrder === 1) {
      return 'Procurement Officer';
    }
    if (stepOrder === 2) {
      return 'Secretary';
    }
    if (stepOrder === 3) {
      return 'Vice Chairman';
    }

    const role = (this.getWorkflowValue(step, ['requiredRole', 'RequiredRole']) || '').toString().replace(/\s+/g, '').toLowerCase();
    if (role === 'procurmentofficer' || role === 'procurementofficer') {
      return 'Procurement Officer';
    }
    if (role === 'secretary') {
      return 'Secretary';
    }
    if (role === 'vicechairman') {
      return 'Vice Chairman';
    }

    return '';
  }

  private getCurrentUserRoleName(): string {
    const rawUser = localStorage.getItem('user');
    let role = '';

    if (rawUser) {
      try {
        const user = JSON.parse(rawUser);
        role = (user?.Role || user?.role || '').toString();
      } catch {
        role = '';
      }
    }

    if (!role) {
      const token = localStorage.getItem('QNBNToken') || localStorage.getItem('token') || localStorage.getItem('access_token') || '';
      role = this.getRoleFromJwt(token);
    }

    const normalized = role.replace(/\s+/g, '').toLowerCase();
    if (normalized === 'procurmentofficer' || normalized === 'procurementofficer') {
      return 'procurementofficer';
    }

    return normalized;
  }

  private getRoleFromJwt(token: string): string {
    if (!token || token.split('.').length < 2) {
      return '';
    }

    try {
      const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');
      const payload = JSON.parse(atob(padded));
      return (payload?.Role || payload?.role || payload?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || '').toString();
    } catch {
      return '';
    }
  }

  async createWorkflowChildTask(): Promise<void> {
    if (!this.canCreateWorkflowChildTask) {
      return;
    }

    const assignedToEmail = this.workflowChildTaskForm.assignedToEmail.trim();
    if (!assignedToEmail) {
      this.toasterService.showError('Assigned user email is required', 'Error');
      return;
    }
    if (!assignedToEmail.toLowerCase().endsWith('@bidaya.com')) {
      this.toasterService.showError('Assigned user email must be a Bidaya email address', 'Error');
      return;
    }

    this._loaderService.showLoader();
    try {
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.VENDOR}/${AppConstants.ACTIONS.Admin_CreateVendorWorkflowChildTask}?vendorId=${this.vendorId}`;
      const payload = {
        assignedToEmail,
        title: this.workflowChildTaskForm.title.trim(),
        description: this.workflowChildTaskForm.description.trim()
      };
      const response: any = await lastValueFrom(this.apiService.postByUrl(url, payload));

      if (response?.isSuccess || response?.IsSuccess) {
        this.toasterService.showSuccess('Child task created successfully', 'Success');
        this.workflowChildTaskForm = { assignedToEmail: '', title: '', description: '' };
        this.showWorkflowTaskModal = false;
        this.loadedTabs.delete('Workflow');
        await this.fetchSection('Workflow');
        this.loadedTabs.add('Workflow');
      } else {
        this.toasterService.showError(response?.errorMessageEn || response?.ErrorMessageEn || 'Failed to create child task', 'Error');
      }
    } catch (error: any) {
      this.toasterService.showError(error?.error?.errorMessageEn || error?.error?.ErrorMessageEn || 'An error occurred while creating child task', 'Error');
    } finally {
      this._loaderService.hideLoader();
      this.cdr.markForCheck();
    }
  }

  isWorkflowChildTaskClosed(task: any): boolean {
    const status = (this.getWorkflowValue(task, ['status', 'Status']) || '').toString().toLowerCase();
    return status === 'completed' || status === 'approved' || status === 'rejected';
  }

  getWorkflowTaskStatusClass(task: any): string {
    const status = (this.getWorkflowValue(task, ['status', 'Status']) || '').toString().trim().toLowerCase();

    if (status === 'approved') {
      return 'approved';
    }
    if (status === 'rejected') {
      return 'rejected';
    }

    return 'pending';
  }

  private extractPublicBankAccounts(vendor: any): any[] {
    const bankAccounts = vendor?.bankAccounts || vendor?.BankAccounts;
    if (Array.isArray(bankAccounts)) {
      return bankAccounts;
    }

    const bankAccount = vendor?.bankAccount || vendor?.BankAccount;
    return bankAccount ? [bankAccount] : [];
  }

  openRejectWorkflowChildTask(task: any): void {
    const canComplete = this.getWorkflowValue(task, ['canComplete', 'CanComplete']) === true;
    if (!canComplete) {
      return;
    }

    this.rejectTargetType = 'workflowTask';
    this.rejectTargetBankAccount = null;
    this.rejectTargetWorkflowTask = task;
    this.rejectionComments = '';
    this.showRejectModal = true;
    this.cdr.markForCheck();
  }

  async completeWorkflowChildTask(task: any, decision: 'Approved' | 'Rejected' = 'Approved', comments = ''): Promise<void> {
    const taskId = this.getWorkflowValue(task, ['id', 'Id', 'ID']);
    const canComplete = this.getWorkflowValue(task, ['canComplete', 'CanComplete']) === true;
    if (!taskId || !canComplete) {
      return;
    }

    this._loaderService.showLoader();
    try {
      const url = this.isWorkflowTaskView && this.childTaskToken
        ? `${environment.API_URL}/api/${AppConstants.CONTROLLERS.VENDOR}/${AppConstants.ACTIONS.Public_CompleteVendorWorkflowChildTask}?vendorId=${this.vendorId}&taskId=${encodeURIComponent(taskId)}&taskToken=${encodeURIComponent(this.childTaskToken)}`
        : `${environment.API_URL}/api/${AppConstants.CONTROLLERS.VENDOR}/${AppConstants.ACTIONS.Admin_CompleteVendorWorkflowChildTask}?taskId=${encodeURIComponent(taskId)}`;
      const payload: any = { decision };
      if (comments.trim()) {
        payload.comments = comments.trim();
      }
      const response: any = await lastValueFrom(this.apiService.postByUrl(url, payload));

      if (response?.isSuccess || response?.IsSuccess) {
        this.toasterService.showSuccess(`Workflow task ${decision.toLowerCase()} successfully`, 'Success');
        if (this.rejectTargetType === 'workflowTask') {
          this.closeRejectModal();
        }
        this.loadedTabs.delete('Workflow');
        await this.fetchSection('Workflow');
        this.loadedTabs.add('Workflow');
      } else {
        this.toasterService.showError(response?.errorMessageEn || response?.ErrorMessageEn || 'Failed to complete workflow task', 'Error');
      }
    } catch (error: any) {
      this.toasterService.showError(error?.error?.errorMessageEn || error?.error?.ErrorMessageEn || 'An error occurred while completing workflow task', 'Error');
    } finally {
      this._loaderService.hideLoader();
      this.cdr.markForCheck();
    }
  }

  async confirmSendWorkflowReminder(): Promise<void> {
    if (!this.canSendWorkflowReminder) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '520px',
      data: {
        title: 'Send Approval Reminder',
        message: `Send an approval reminder to the ${this.currentWorkflowApprovalStepName}?`,
        confirmText: 'Send Reminder',
        cancelText: 'Cancel'
      }
    });

    const confirmed = await lastValueFrom(dialogRef.afterClosed());
    if (!confirmed) {
      return;
    }

    await this.sendWorkflowReminder();
  }

  private async sendWorkflowReminder(): Promise<void> {
    this._loaderService.showLoader();
    try {
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.VENDOR}/${AppConstants.ACTIONS.Admin_SendVendorWorkflowReminder}?vendorId=${encodeURIComponent(this.vendorId)}`;
      const response: any = await lastValueFrom(this.apiService.postByUrl(url, {}));

      if (response?.isSuccess || response?.IsSuccess) {
        this.toasterService.showSuccess('Approval reminder sent successfully', 'Success');
      } else {
        this.toasterService.showError(response?.errorMessageEn || response?.ErrorMessageEn || 'Failed to send approval reminder', 'Error');
      }
    } catch (error: any) {
      this.toasterService.showError(error?.error?.errorMessageEn || error?.error?.ErrorMessageEn || 'An error occurred while sending the reminder', 'Error');
    } finally {
      this._loaderService.hideLoader();
      this.cdr.markForCheck();
    }
  }
  async approveVendor(): Promise<void> {
    if (!this.canApproveVendorWorkflow) {
      return;
    }

    this._loaderService.showLoader();
    try {
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.VENDOR}/${AppConstants.ACTIONS.Admin_ApproveVendor}?vendorId=${this.vendorId}`;
      const response: any = await lastValueFrom(this.apiService.postByUrl(url, {}));

      if (response?.isSuccess || response?.IsSuccess) {
        this.toasterService.showSuccess('Vendor approval submitted successfully', 'Success');
        this.loadedTabs.delete('Workflow');
        this.loadedTabs.delete('Application');
        await this.fetchSection('Workflow');
        await this.fetchSection('Application');
        this.loadedTabs.add('Workflow');
        this.loadedTabs.add('Application');
      } else {
        this.toasterService.showError(response?.errorMessageEn || response?.ErrorMessageEn || 'Failed to approve vendor', 'Error');
      }
    } catch (error: any) {
      this.toasterService.showError(error?.error?.errorMessageEn || error?.error?.ErrorMessageEn || 'An error occurred while approving vendor', 'Error');
    } finally {
      this._loaderService.hideLoader();
      this.cdr.markForCheck();
    }
  }

  openRejectVendor(): void {
    if (!this.canRejectVendorWorkflow) {
      return;
    }

    this.rejectTargetType = 'vendor';
    this.rejectTargetBankAccount = null;
    this.rejectTargetWorkflowTask = null;
    this.rejectionComments = '';
    this.showRejectModal = true;
    this.cdr.markForCheck();
  }

  openChangeRequestVendor(): void {
    if (!this.canRequestVendorChange) {
      return;
    }

    this.rejectTargetType = 'changeRequest';
    this.rejectTargetBankAccount = null;
    this.rejectTargetWorkflowTask = null;
    this.rejectionComments = '';
    this.showRejectModal = true;
    this.cdr.markForCheck();
  }

  closeRejectModal(): void {
    this.showRejectModal = false;
    this.rejectTargetBankAccount = null;
    this.rejectTargetWorkflowTask = null;
    this.rejectionComments = '';
    this.cdr.markForCheck();
  }

  getRejectModalTitle(): string {
    if (this.rejectTargetType === 'bank') {
      return 'Reject Bank Account';
    }

    if (this.rejectTargetType === 'workflowTask') {
      return 'Reject Child Task';
    }

    if (this.rejectTargetType === 'changeRequest') {
      return 'Request Changes';
    }

    return 'Reject Vendor';
  }

  async submitRejection(): Promise<void> {
    const comments = this.rejectionComments.trim();
    if (!comments) {
      this.toasterService.showError('Comments are required', 'Validation Error');
      return;
    }

    if (this.rejectTargetType === 'bank') {
      await this.rejectBankAccount(comments);
    } else if (this.rejectTargetType === 'workflowTask') {
      await this.completeWorkflowChildTask(this.rejectTargetWorkflowTask, 'Rejected', comments);
    } else if (this.rejectTargetType === 'changeRequest') {
      await this.requestVendorChange(comments);
    } else {
      await this.rejectVendor(comments);
    }
  }

  getRejectConfirmText(): string {
    return this.rejectTargetType === 'changeRequest' ? 'Request Changes' : 'Reject';
  }

  private async requestVendorChange(comments: string): Promise<void> {
    this._loaderService.showLoader();
    try {
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.VENDOR}/${AppConstants.ACTIONS.Admin_RequestVendorChange}?vendorId=${this.vendorId}`;
      const response: any = await lastValueFrom(this.apiService.postByUrl(url, { reason: comments, comments }));

      if (response?.isSuccess || response?.IsSuccess) {
        this.toasterService.showSuccess('Change request submitted successfully', 'Success');
        this.closeRejectModal();
        this.loadedTabs.delete('Workflow');
        this.loadedTabs.delete('Application');
        await this.fetchSection('Workflow');
        await this.fetchSection('Application');
        this.loadedTabs.add('Workflow');
        this.loadedTabs.add('Application');
      } else {
        this.toasterService.showError(response?.errorMessageEn || response?.ErrorMessageEn || 'Failed to request changes', 'Error');
      }
    } catch (error: any) {
      this.toasterService.showError(error?.error?.errorMessageEn || error?.error?.ErrorMessageEn || 'An error occurred while requesting changes', 'Error');
    } finally {
      this._loaderService.hideLoader();
      this.cdr.markForCheck();
    }
  }
  private async rejectVendor(comments: string): Promise<void> {
    this._loaderService.showLoader();
    try {
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.VENDOR}/${AppConstants.ACTIONS.Admin_RejectVendor}?vendorId=${this.vendorId}`;
      const response: any = await lastValueFrom(this.apiService.postByUrl(url, { reason: comments, comments }));

      if (response?.isSuccess || response?.IsSuccess) {
        this.toasterService.showSuccess('Vendor rejected successfully', 'Success');
        this.closeRejectModal();
        this.loadedTabs.delete('Workflow');
        this.loadedTabs.delete('Application');
        this.loadedTabs.delete('BankAccounts');
        await this.fetchSection('Workflow');
        await this.fetchSection('Application');
        await this.fetchBankAccounts();
        this.loadedTabs.add('Workflow');
        this.loadedTabs.add('Application');
        this.loadedTabs.add('BankAccounts');
      } else {
        this.toasterService.showError(response?.errorMessageEn || response?.ErrorMessageEn || 'Failed to reject vendor', 'Error');
      }
    } catch (error: any) {
      this.toasterService.showError(error?.error?.errorMessageEn || error?.error?.ErrorMessageEn || 'An error occurred while rejecting vendor', 'Error');
    } finally {
      this._loaderService.hideLoader();
      this.cdr.markForCheck();
    }
  }

  private async rejectBankAccount(comments: string): Promise<void> {
    const accountId = this.getBankAccountApprovalId(this.rejectTargetBankAccount);
    if (!accountId) {
      this.toasterService.showError('Bank account number is missing', 'Error');
      return;
    }

    this._loaderService.showLoader();
    try {
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.BankAPI}/${AppConstants.ACTIONS.Admin_RejectBankAccount}?vendorId=${this.vendorId}&accountId=${encodeURIComponent(accountId)}`;
      const response: any = await lastValueFrom(this.apiService.postByUrl(url, { reason: comments, comments }));

      if (response?.isSuccess || response?.IsSuccess) {
        this.toasterService.showSuccess('Bank account rejected successfully', 'Success');
        this.closeRejectModal();
        this.loadedTabs.delete('BankAccounts');
        await this.fetchBankAccounts();
        this.loadedTabs.add('BankAccounts');
      } else {
        this.toasterService.showError(response?.errorMessageEn || response?.ErrorMessageEn || 'Failed to reject bank account', 'Error');
      }
    } catch (error: any) {
      this.toasterService.showError(error?.error?.errorMessageEn || error?.error?.ErrorMessageEn || 'An error occurred while rejecting bank account', 'Error');
    } finally {
      this._loaderService.hideLoader();
      this.cdr.markForCheck();
    }
  }

  openAddContact(): void {
    const defaultContactType = this.hasPrimaryContact() ? 'Secondary' : 'Primary';
    this.contactModalMode = 'add';
    this.editingContactId = null;
    this.contactForm = {
      name: '',
      title: '',
      email: '',
      mobileNumber: '',
      landlineNumber: '',
      contactType: defaultContactType,
      password: '',
      confirmPassword: ''
    };
    this.showPassword = false;
    this.showConfirmPassword = false;
    this.showContactModal = true;
    this.cdr.markForCheck();
  }

  openEditContact(contact: any): void {
    this.contactModalMode = 'edit';
    this.editingContactId = contact.id ?? contact.ID ?? null;
    this.contactForm = {
      name: contact.name ?? '',
      title: contact.title ?? '',
      email: contact.email ?? '',
      mobileNumber: contact.mobileNumber ?? '',
      landlineNumber: contact.landlineNumber ?? '',
      contactType: contact.contactType ?? 'Secondary',
      password: '',
      confirmPassword: ''
    };
    this.showContactModal = true;
    this.cdr.markForCheck();
  }

  closeContactModal(): void {
    this.showContactModal = false;
    this.cdr.markForCheck();
  }

  async saveContact(): Promise<void> {
    if (!this.contactForm.email?.trim()) {
      this.toasterService.showError('Email is required', 'Validation Error');
      return;
    }

    if (this.contactModalMode === 'add' && this.contactForm.contactType === 'Primary' && this.hasPrimaryContact()) {
      this.toasterService.showError('This vendor already has a primary contact', 'Validation Error');
      return;
    }

    if (this.contactModalMode === 'add' && !this.validatePasswordFields(this.contactForm.password, this.contactForm.confirmPassword, false)) {
      return;
    }

    this._loaderService.showLoader();
    try {
      let url: string;
      let response: any;
      const payload = {
        ...this.contactForm,
        password: this.contactForm.password || undefined,
        confirmPassword: this.contactForm.confirmPassword || undefined
      };

      if (this.contactModalMode === 'add') {
        url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.VENDOR}/${AppConstants.ACTIONS.AddContactPerson}?vendorId=${this.vendorId}`;
        response = await lastValueFrom(this.apiService.postByUrl(url, payload));
      } else {
        url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.VENDOR}/${AppConstants.ACTIONS.UpdateContactPerson}?vendorId=${this.vendorId}&contactId=${this.editingContactId}`;
        response = await lastValueFrom(this.apiService.putByUrl(url, payload));
      }

      if (response?.isSuccess || response?.IsSuccess) {
        this.toasterService.showSuccess(
          this.contactModalMode === 'add' ? 'Contact added successfully' : 'Contact updated successfully',
          'Success'
        );
        this.closeContactModal();
        await this.refreshContacts();
      } else {
        this.toasterService.showError(response?.errorMessageEn || 'Operation failed', 'Error');
      }
    } catch (error: any) {
      this.toasterService.showError(error?.error?.errorMessageEn || 'An error occurred', 'Error');
    } finally {
      this._loaderService.hideLoader();
    }
  }

  openPasswordModal(contact: any): void {
    const contactId = this.getContactId(contact);
    if (!contactId) {
      this.toasterService.showError('Contact id is missing', 'Error');
      return;
    }

    this.passwordTargetContact = contact;
    this.passwordForm = { password: '', confirmPassword: '' };
    this.showPassword = false;
    this.showConfirmPassword = false;
    this.showPasswordModal = true;
    this.cdr.markForCheck();
  }

  closePasswordModal(): void {
    this.showPasswordModal = false;
    this.passwordTargetContact = null;
    this.passwordForm = { password: '', confirmPassword: '' };
    this.cdr.markForCheck();
  }

  async saveContactPassword(): Promise<void> {
    if (!this.validatePasswordFields(this.passwordForm.password, this.passwordForm.confirmPassword, true)) {
      return;
    }

    const contactId = this.getContactId(this.passwordTargetContact);
    if (!contactId) {
      this.toasterService.showError('Contact id is missing', 'Error');
      return;
    }

    this._loaderService.showLoader();
    try {
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.VENDOR}/${AppConstants.ACTIONS.UpdateContactPassword}?vendorId=${this.vendorId}&contactId=${contactId}`;
      const response: any = await lastValueFrom(this.apiService.putByUrl(url, {
        password: this.passwordForm.password,
        confirmPassword: this.passwordForm.confirmPassword
      }));

      if (response?.isSuccess || response?.IsSuccess) {
        this.toasterService.showSuccess('Password updated successfully', 'Success');
        this.closePasswordModal();
        await this.refreshContacts();
      } else {
        this.toasterService.showError(response?.errorMessageEn || response?.ErrorMessageEn || 'Failed to update password', 'Error');
      }
    } catch (error: any) {
      this.toasterService.showError(error?.error?.errorMessageEn || error?.error?.ErrorMessageEn || 'An error occurred while updating password', 'Error');
    } finally {
      this._loaderService.hideLoader();
      this.cdr.markForCheck();
    }
  }

  canInviteContact(contact: any): boolean {
    const showInviteButton = contact?.showInviteButton ?? contact?.ShowInviteButton;
    return !!this.getContactId(contact) && showInviteButton === true;
  }

  async confirmInviteContact(contact: any): Promise<void> {
    const contactId = this.getContactId(contact);
    if (!contactId) {
      this.toasterService.showError('Contact id is missing', 'Error');
      return;
    }

    if (!this.canInviteContact(contact)) {
      this.toasterService.showError('This contact already has a password', 'Error');
      return;
    }

    const contactName = contact?.name || contact?.Name || contact?.email || contact?.Email || 'this contact';
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '520px',
      data: {
        title: 'Send Contact Invitation',
        message: `Are you sure you want to send a vendor portal invitation to ${contactName}?`,
        confirmText: 'Yes, Send',
        cancelText: 'Cancel'
      }
    });

    const confirmed = await lastValueFrom(dialogRef.afterClosed());
    if (!confirmed) {
      return;
    }

    await this.inviteContact(contactId);
  }

  async inviteContact(contactId: number | string): Promise<void> {
    this._loaderService.showLoader();
    try {
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.VENDOR}/${AppConstants.ACTIONS.InviteContactPerson}?vendorId=${this.vendorId}&contactId=${contactId}`;
      const response: any = await lastValueFrom(this.apiService.postByUrl(url, {}));

      if (response?.isSuccess || response?.IsSuccess) {
        this.toasterService.showSuccess('Contact invitation sent successfully', 'Success');
        await this.refreshContacts();
      } else {
        this.toasterService.showError(response?.errorMessageEn || response?.ErrorMessageEn || 'Failed to send contact invitation', 'Error');
      }
    } catch (error: any) {
      this.toasterService.showError(error?.error?.errorMessageEn || error?.error?.ErrorMessageEn || 'An error occurred while sending invitation', 'Error');
    } finally {
      this._loaderService.hideLoader();
      this.cdr.markForCheck();
    }
  }

  openRemoveContactModal(contact: any): void {
    const contactId = this.getContactId(contact);
    if (!contactId) {
      this.toasterService.showError('Contact id is missing', 'Error');
      return;
    }

    this.removeTargetContact = contact;
    this.showRemoveContactModal = true;
    this.cdr.markForCheck();
  }

  closeRemoveContactModal(): void {
    this.showRemoveContactModal = false;
    this.removeTargetContact = null;
    this.cdr.markForCheck();
  }

  getRemoveContactDisplayName(): string {
    return this.removeTargetContact?.name ||
      this.removeTargetContact?.Name ||
      this.removeTargetContact?.email ||
      this.removeTargetContact?.Email ||
      'this contact';
  }

  async confirmRemoveContact(): Promise<void> {
    const contactId = this.getContactId(this.removeTargetContact);
    if (!contactId) {
      this.toasterService.showError('Contact id is missing', 'Error');
      return;
    }

    this._loaderService.showLoader();
    try {
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.VENDOR}/${AppConstants.ACTIONS.RemoveContactPerson}?vendorId=${this.vendorId}&contactId=${contactId}`;
      const response: any = await lastValueFrom(this.apiService.deleteByUrl(url, null, null));

      if (response?.isSuccess || response?.IsSuccess) {
        this.toasterService.showSuccess('Contact removed successfully', 'Success');
        this.closeRemoveContactModal();
        await this.refreshContacts();
      } else {
        this.toasterService.showError(response?.errorMessageEn || response?.ErrorMessageEn || 'Failed to remove contact', 'Error');
      }
    } catch (error: any) {
      this.toasterService.showError(error?.error?.errorMessageEn || error?.error?.ErrorMessageEn || 'An error occurred while removing contact', 'Error');
    } finally {
      this._loaderService.hideLoader();
      this.cdr.markForCheck();
    }
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
    this.cdr.markForCheck();
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
    this.cdr.markForCheck();
  }

  async setContactAsPrimary(contact: any): Promise<void> {
    const contactId = this.getContactId(contact);
    if (!contactId) return;

    if (this.hasPrimaryContact(contactId)) {
      this.toasterService.showError('This vendor already has a primary contact', 'Validation Error');
      return;
    }

    this._loaderService.showLoader();
    try {
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.VENDOR}/${AppConstants.ACTIONS.SetContactAsPrimary}?vendorId=${this.vendorId}&contactId=${contactId}`;
      const response: any = await lastValueFrom(this.apiService.putByUrl(url, {}));
      if (response?.isSuccess || response?.IsSuccess) {
        this.toasterService.showSuccess('Contact set as primary', 'Success');
        await this.refreshContacts();
      } else {
        this.toasterService.showError(response?.errorMessageEn || 'Operation failed', 'Error');
      }
    } catch (error: any) {
      this.toasterService.showError(error?.error?.errorMessageEn || 'An error occurred', 'Error');
    } finally {
      this._loaderService.hideLoader();
    }
  }

  canSetContactAsPrimary(contact: any): boolean {
    return contact?.contactType !== 'Primary' && !this.hasPrimaryContact(this.getContactId(contact));
  }

  async setContactAsSecondary(contact: any): Promise<void> {
    const contactId = this.getContactId(contact);
    if (!contactId) return;
    this._loaderService.showLoader();
    try {
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.VENDOR}/${AppConstants.ACTIONS.SetContactAsSecondary}?vendorId=${this.vendorId}&contactId=${contactId}`;
      const response: any = await lastValueFrom(this.apiService.putByUrl(url, {}));
      if (response?.isSuccess || response?.IsSuccess) {
        this.toasterService.showSuccess('Contact set as secondary', 'Success');
        await this.refreshContacts();
      } else {
        this.toasterService.showError(response?.errorMessageEn || 'Operation failed', 'Error');
      }
    } catch (error: any) {
      this.toasterService.showError(error?.error?.errorMessageEn || 'An error occurred', 'Error');
    } finally {
      this._loaderService.hideLoader();
    }
  }

  private async refreshContacts(): Promise<void> {
    this.loadedTabs.delete('Contacts');
    await this.fetchSection('Contacts');
    this.loadedTabs.add('Contacts');
  }

  private getContactId(contact: any): number | string | null {
    return contact?.id ?? contact?.ID ?? contact?.contactId ?? contact?.ContactId ?? contact?.contactID ?? contact?.ContactID ?? null;
  }

  hasPrimaryContact(excludeContactId: number | string | null = null): boolean {
    const contacts = this.contactsData?.contactPerson || this.contactsData?.ContactPerson || [];
    if (!Array.isArray(contacts)) {
      return false;
    }

    return contacts.some((contact: any) => {
      const contactId = this.getContactId(contact);
      const isExcluded = excludeContactId !== null && contactId?.toString() === excludeContactId.toString();
      const contactType = (contact?.contactType || contact?.ContactType || '').toString().toLowerCase();
      return !isExcluded && contactType === 'primary';
    });
  }

  private validatePasswordFields(password: string, confirmPassword: string, required: boolean): boolean {
    const hasPassword = !!password?.trim();
    const hasConfirmPassword = !!confirmPassword?.trim();

    if (!required && !hasPassword && !hasConfirmPassword) {
      return true;
    }

    if (!hasPassword || !hasConfirmPassword) {
      this.toasterService.showError('Password and confirm password are required', 'Validation Error');
      return false;
    }

    if (password !== confirmPassword) {
      this.toasterService.showError('Password and confirm password do not match', 'Validation Error');
      return false;
    }

    const isStrong = password.length >= 8 &&
      /[A-Z]/.test(password) &&
      /[a-z]/.test(password) &&
      /[0-9]/.test(password) &&
      /[^a-zA-Z0-9]/.test(password);

    if (!isStrong) {
      this.toasterService.showError('Password must be at least 8 characters and include uppercase, lowercase, number, and special character', 'Validation Error');
      return false;
    }

    return true;
  }

  goBack(): void {
    this._router.navigate([`/${AppConstants.ROUTES.VENDORS}`]);
  }

  async confirmInviteVendor(): Promise<void> {
    const vendorName = this.vendorName || this.vendorId || 'this vendor';
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '520px',
      data: {
        title: 'Send Invitation',
        message: `Are you sure you want to send an invitation to ${vendorName}?`,
        confirmText: 'Yes, Send',
        cancelText: 'Cancel'
      }
    });

    const confirmed = await lastValueFrom(dialogRef.afterClosed());
    if (!confirmed) {
      return;
    }

    await this.inviteVendor();
  }

  async inviteVendor(): Promise<void> {
    this._loaderService.showLoader();
    try {
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.VENDOR}/${AppConstants.ACTIONS.InviteVendor}`;
      const response: any = await lastValueFrom(this.apiService.postByUrl(url, { vendorId: this.vendorId }));
      if (response?.isSuccess || response?.IsSuccess) {
        this.toasterService.showSuccess('Vendor invited successfully', 'Success');
      } else {
        const msg = response?.errorMessageEn || 'Failed to invite vendor';
        this.toasterService.showError(msg, 'Error');
      }
    } catch (error: any) {
      this.toasterService.showError(error?.error?.errorMessageEn || 'An error occurred', 'Error');
    } finally {
      this._loaderService.hideLoader();
    }
  }


  getAnnualTurnoverYearNumber(turnover: any): string {
    return this.val(this.getWorkflowValue(turnover, ['yearNumber', 'YearNumber']));
  }

  get annualTurnovers(): any[] {
    const turnovers = this.getWorkflowValue(this.applicationData, ['annualTurnovers', 'AnnualTurnovers']);
    return Array.isArray(turnovers) ? turnovers : [];
  }

  getAnnualTurnoverDisplayYear(turnover: any): string {
    const explicitYear = this.getWorkflowValue(turnover, ['year', 'Year']);
    if (explicitYear && !isNaN(Number(explicitYear)) && Number(explicitYear) > 1900) {
      return String(explicitYear);
    }

    const currentYear = this.getWorkflowValue(this.applicationData, ['currentYear', 'CurrentYear']);
    const yearNumber = this.getWorkflowValue(turnover, ['yearNumber', 'YearNumber']);
    if (!currentYear || !yearNumber || isNaN(Number(currentYear)) || isNaN(Number(yearNumber))) {
      return '-';
    }

    const calculatedYear = Number(currentYear) - Number(yearNumber);
    return calculatedYear > 0 ? String(calculatedYear) : '-';
  }
  formatDate(val: string | null): string {
    if (!val) return '-';
    const d = new Date(val);
    return isNaN(d.getTime()) ? val : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  val(v: any): string {
    if (v === null || v === undefined || v === '') return '-';
    return String(v);
  }

  getWorkflowValue(source: any, keys: string[]): any {
    for (const key of keys) {
      const value = source?.[key];
      if (value !== null && value !== undefined && value !== '') {
        return value;
      }
    }
    return null;
  }

  private pickValue(source: any, keys: string[]): string {
    for (const key of keys) {
      const value = source?.[key];
      if (value !== null && value !== undefined && value !== '') {
        return String(value);
      }
    }
    return '';
  }
}
